import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OwnerHeader } from "./OwnerHeader";
import { OwnerSidebar } from "./OwnerSidebar";
import { useSidebar } from "@stores/sidebar/useSidebar";
import { useScrollToTop } from "@hooks/useScrollToTop";
import { useServerSettingsSync } from "@features/settings/useOwnerSettings";
import { ErrorBoundary } from "@components/ErrorBoundary";
import { navKeyForPath } from "@lib/pageTitle";
import { NotificationStreamProvider } from "@components/notifications/NotificationStreamProvider";
import { clsx } from "clsx";
import { useLocation } from "react-router-dom";

export const OwnerLayout: React.FC = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { isCollapsed, toggleCollapse } = useSidebar();
    const { pathname } = useLocation();
    useScrollToTop({ pathname });
    useServerSettingsSync();

    const { t, i18n } = useTranslation();
    const navKey = navKeyForPath(pathname);
    React.useEffect(() => {
        const brand = t("common.brand");
        document.title = navKey ? `${t(`nav.${navKey}`)} · ${brand}` : brand;
    }, [navKey, i18n.language, t]);

    return (
        <NotificationStreamProvider>
            <div className="min-h-screen bg-gradient-to-br from-neutral-light via-white to-neutral-light/50 dark:from-neutral-dark dark:via-neutral-dark dark:to-neutral-dark/80 transition-colors duration-300">
                <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(31,182,255,0.03),transparent_50%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(31,182,255,0.05),transparent_50%)] pointer-events-none" />
                <OwnerHeader onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
                <div className="flex relative">
                    <OwnerSidebar
                        isMobileOpen={isMobileMenuOpen}
                        onMobileClose={() => setIsMobileMenuOpen(false)}
                        isCollapsed={isCollapsed}
                        onToggleCollapse={toggleCollapse}
                    />
                    <main
                        className={clsx(
                            "flex-1 min-w-0 pt-6 pb-12 px-4 sm:px-6 lg:px-8 xl:px-10 transition-all duration-300 min-h-[calc(100vh-4rem)] relative z-10",
                            isCollapsed ? "lg:ms-20" : "lg:ms-72"
                        )}
                    >
                        <div className="max-w-7xl mx-auto">
                            <ErrorBoundary key={pathname}>
                                <Outlet />
                            </ErrorBoundary>
                        </div>
                    </main>
                </div>
            </div>
        </NotificationStreamProvider>
    );
};
